import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { adminLogin, adminMe, getAdminToken, clearAdminToken } from '../lib/adminApi';
import { ShieldCheck, LogIn, Loader2 } from 'lucide-react';

export default function AdminLogin() {
  const nav = useNavigate();
  const { toast } = useToast();
  const [form, setForm] = useState({ email: 'admin@waantravel.com', password: '' });
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    (async () => {
      if (getAdminToken()) {
        try { await adminMe(); nav('/admin'); return; } catch { clearAdminToken(); }
      }
      setChecking(false);
    })();
  }, [nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await adminLogin(form.email, form.password);
      if (res.admin.mustChangePassword) nav('/admin/change-password');
      else nav('/admin');
    } catch (err) {
      toast({ title: 'Login failed', description: err?.response?.data?.detail || 'Please try again.' });
    } finally { setBusy(false); }
  };

  if (checking) return <div className="min-h-screen grid place-items-center text-neutral-500"><Loader2 className="animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-neutral-950 text-white grid place-items-center p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-6">
          <div className="relative w-11 h-11 rounded-lg bg-white text-teal-900 grid place-items-center">
            <span className="font-serif text-xl">W</span>
            <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-red-500 border-2 border-neutral-950"></span>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.14em] text-emerald-300">Waan Travel</div>
            <div className="font-display text-xl">Admin Console</div>
          </div>
        </div>
        <div className="rounded-2xl bg-white text-neutral-900 p-7 shadow-2xl">
          <div className="flex items-center gap-2 text-teal-800"><ShieldCheck size={16} /><span className="text-xs uppercase tracking-widest">Restricted area</span></div>
          <h1 className="font-display text-2xl mt-2">Sign in to the console</h1>
          <p className="text-sm text-neutral-500 mt-1">Internal staff only.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div><Label>Email</Label><Input type="email" className="h-11 mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-login-email" /></div>
            <div><Label>Password</Label><Input type="password" className="h-11 mt-1.5" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="admin-login-password" /></div>
            <Button type="submit" disabled={busy} className="w-full h-11 bg-teal-800 hover:bg-teal-900 text-white" data-testid="admin-login-submit">{busy ? (<><Loader2 size={16} className="mr-1.5 animate-spin" /> Signing in…</>) : (<><LogIn size={16} className="mr-1.5" /> Sign in</>)}</Button>
            <div className="text-center pt-1">
              <a href="/admin/forgot-password" className="text-xs text-teal-800 hover:text-teal-900 underline underline-offset-2" data-testid="admin-forgot-password-link">Forgot password?</a>
            </div>
          </form>
        </div>
        <div className="text-[11px] text-neutral-500 text-center mt-4">Actions in this console are logged for audit.</div>
      </div>
    </div>
  );
}
